import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  BehaviorSubject,
  firstValueFrom,
  lastValueFrom,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { BusinessDataFormObject } from 'src/app/shared/models/business-data/business-data-form-object/business-data-form-object';
import { BusinessDataLabels } from 'src/app/shared/models/business-data/business-data-labels/business-data-labels';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { BusinessEquipmentLabels } from 'src/app/shared/models/business-equipment/business-equipment-labels/business-equipment-labels';
import { BusinessRepresentativeFormObject } from 'src/app/shared/models/business-representative/business-representative-form-object/business-representative-form-object';
import { BusinessRepresentativeLabels } from 'src/app/shared/models/business-representative/business-representative-labels/business-representative-labels';
import { BusinessResponseDTO } from 'src/app/shared/models/dtos/register-form/business-response-dto/business-response-dto';
import { Equipment } from 'src/app/shared/models/dtos/register-form/equipment/equipment';
import { Account } from 'src/app/shared/models/dtos/account/account';
import {
  asyncValidator,
  emailPattern,
  passwordValidator,
  postalCodeValidator,
  urlValidator,
  errorObjectBuilder,
  genericRegisterValidator,
  serialValidator,
} from 'src/app/shared/validators/registration-validators';
import { sameValueTwoFields } from 'src/app/utils/form-validator';
import { TranslationService } from '../translation/translation.service';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { BusinessDataOptions } from 'src/app/shared/models/business-data/business-data-options/business-data-options';
import { BusinessDTO } from 'src/app/shared/models/dtos/requests/business-dto/business-dto';
import { UserService } from '../../api/user/user.service';
import { ProductService } from '../../api/product/product.service';
import { BusinessType } from 'src/app/shared/models/constants/business-type';
import { HashValidationDTO } from 'src/app/shared/models/dtos/responses/hash-validation-dto/hash-validation-dto';
import { TopHeaderService } from '../top-header/top-header.service';
import { BusinessTypeModel } from 'src/app/shared/models/business-type-model/business-type-model';
import { CountryList } from 'src/app/shared/models/dtos/register-form/country-list/country-list';
import { getTranslocoLangToBackEnd } from 'src/app/utils/translocoLang';

import phonePrefixes from '../../../../assets/phone-prefixes/phone-prefixes.json';
import { SerialModelValidationStatus } from 'src/app/shared/models/dtos/responses/serial-model-validation-response-dto/serial-model-validation-status';
import { DialogService } from '../dialog/dialog.service';
import { TBusinessEquipmentFormPhase } from 'src/app/shared/components/molecules/business-equipment-form/business-equipment-form.component';
import {
  InvalidSerialException,
  InvalidSerialNumberException,
} from 'src/app/shared/errors/product-service/product-service-errors';
import { MigrationStatus } from 'src/app/shared/models/constants/migration-status';

export type TRegisterEquipmentFormGroup = FormGroup<TRegisterEquipmentForm>;

export type TRegisterEquipmentForm = {
  placeOfPurchase: FormControl<string | null>;
  installationDate: FormControl<Date | null>;
  businessName: FormControl<string | null>;
  representativeName: FormControl<string | null>;
  representativeLastName: FormControl<string | null>;
  businessEmail: FormControl<string | null>;
  country: FormControl<string | number | null>;
  stateOrRegion: FormControl<string | null>;
  primaryPhone: FormControl<string | null>;
  phoneType: FormControl<'mobile' | 'land' | null>;
  agree: FormControl<boolean | null>;
};

export type TCountryEquipmentFormGroup = FormGroup<TCountryEquipmentForm>;

export type TCountryEquipmentForm = {
  country: FormControl<string | number | null>;
};

@Injectable({
  providedIn: 'root',
})
/**
 * Holds information about ongoing registration
 */
export class RegisterService implements OnDestroy {
  private _businessDataFormGroup!: UntypedFormGroup;
  private _equipmentFormGroup!: UntypedFormGroup;
  private _businessRepresentativeFormGroup!: UntypedFormGroup;
  activeStep: 1 | 2 | 3 = 1;
  private _businessDataFormObject!: BusinessDataFormObject;
  private _businessEquipmentFormObject!: BusinessEquipmentFormObject;
  private _businessRepresentativeFormObject!: BusinessRepresentativeFormObject;
  inviteNewBusiness?: BusinessResponseDTO;
  inviteCurrentBusiness?: BusinessResponseDTO;
  invitedRepresentativeEmail?: string;
  loading: boolean = false;
  readonly routingGuards = {
    expiredInvite: false,
  };
  businessHashValidation?: HashValidationDTO;
  businessTypesForSelect!: BusinessTypeModel[];
  errorMessages = {
    cantRegisterInvalidForm: 'cant-register-invalid-form-error',
    equipmentAlreadyExists: 'equipment-exists-error',
    errorTryingRegister: 'trying-register-user-error',
    cantInstatiateUser: 'cant-instantiate-user-error',
    candUpdateInvalidForm: 'cant-update-invalid-form-error',
    tryingUpdateBusinessError: 'trying-update-business-error',
    somethingWrong: 'something-wrong',
  };
  private _countries: CountryList[] = [];
  private _registerEquipmentFormGroup!: TRegisterEquipmentFormGroup;
  private readonly subsink = new Subscription();
  readonly labels = {
    couldNotCheckPrinterSerialData: 'unable-check-printer-serial-number',
    noCommPrinterDatabase: 'could-not-communicate-with-printer-database',
    couldNotRegister: 'cannot-register-printer-serial-number',
    printerAlreadyRegitered: 'printer-already-registered',
    invalidModelNumber: 'model-not-found',
    invalidSerialNumber: 'invalid-serial-number',
    ok: 'ok',
  };
  businessEquipmentFormPhase: TBusinessEquipmentFormPhase = 'LNR_REGISTER';
  equipmentRegisterFeedback?: 'eligible' | 'registered';
  disableCountryField = false;
  private _countryEquipmentFormGroup!: TCountryEquipmentFormGroup;
  private hashValidationDTOSubject: BehaviorSubject<HashValidationDTO | null> =
    new BehaviorSubject<HashValidationDTO | null>(null);
  public _serialNumber: any = '';

  /**
   * Gets the current serial number.
   * @return {String} The serial number as a string.
   */
  getSerialNumber(): string {
    return this._serialNumber;
  }
  /**
   * Sets the serial number.
   * @param {string} value - The new serial number to assign.
   */
  setSerialNumber(value: string) {
    this._serialNumber = value as string;
  }
  /**
   * @param {HashValidationDTO} data
   */
  sendHashValidationDTO(data: HashValidationDTO) {
    this.hashValidationDTOSubject.next(data);
  }

  /**
   * @return {Observable<HashValidationDTO>}
   */
  getHashValidationDTO(): Observable<HashValidationDTO | null> {
    return this.hashValidationDTOSubject.asObservable();
  }

  /**
   * List of available countries for LnR
   */
  get countries() {
    return this._countries;
  }

  /**
   * Register Equipment Form Group global instance
   */
  get registerEquipmentFormGroup() {
    return this._registerEquipmentFormGroup;
  }

  /**
   * Register Equipment Form Group global instance
   */
  get countryEquipmentFormGroup() {
    return this._countryEquipmentFormGroup;
  }
  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private productService: ProductService,
    private fb: UntypedFormBuilder,
    private translationService: TranslationService,
    private readonly topHeaderService: TopHeaderService,
    private dialogService: DialogService
  ) {
    this.initializeFormObjects();
    this.subsink.add(this.translationService.translateObject$(this.labels));
    this.subsink.add(translationService.translateObject$(this.errorMessages));
  }

  /**
   * avoid memory leak (perhaps redundant)
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /* eslint-disable-next-line */
  getBusinessDataFormObject(
    businessId?: number,
    completed?: (formGroup: UntypedFormGroup) => any
  ) {
    this.initBusinessTypes();
    if (businessId == null) {
      this.userService.listCountries().subscribe((countries) => {
        const countryList = countries.map((a) => a.countries).flat();
        countryList.sort((a, b) => a.name.localeCompare(b.name));
        this._countries = countryList;
        this._businessDataFormObject.options = new BusinessDataOptions(
          countryList,
          this.businessTypesForSelect
        );
      });
      return this._businessDataFormObject;
    }
    this.userService
      .getBusinessInfo()
      .pipe(take(1))
      .subscribe((business) => {
        if (!business) {
          return;
        }
        const formGroup = this._businessDataFormObject.formGroup;
        formGroup.get('businessName')!.setValue(business.businessName);
        formGroup.get('businessAddress')!.setValue(business.address);
        formGroup.get('number')!.setValue(business.addressNumber);
        formGroup.get('country')!.setValue(business.country!.id);
        if (
          business.state === '' &&
          business.migrationStatus !== MigrationStatus.ACTIVE
        ) {
          formGroup.get('state')!.setValue('N/A');
        } else {
          formGroup.get('state')!.setValue(business.state);
        }
        formGroup.get('postalCode')!.setValue(business.postalCode);
        formGroup.get('phoneNumber')!.setValue(business.phoneNumber);
        formGroup.get('typeOfBusiness')!.setValue(business.businessType);
        formGroup.get('businessContactEmail')!.setValue(business.email);
        formGroup.get('businessWebsite')!.setValue(business.website);
        formGroup.get('picture')!.setValue(business.imgSource);
        formGroup.get('businessLevel')!.setValue(business.businessLevel!.id);

        formGroup.enable();

        if (completed != null) {
          completed(formGroup);
        }
      });

    return this._businessDataFormObject;
  }

  /**
   * Initialize and set business type language
   */
  private initBusinessTypes() {
    const businessTypes = {
      directToGarment: 'direct-to-garment',
      signage: 'signage',
      sublimation: 'sublimation',
    };
    this.businessTypesForSelect = [
      new BusinessTypeModel(
        BusinessType.DIRECT_TO_GARMENT,
        businessTypes.directToGarment
      ),
      new BusinessTypeModel(BusinessType.SIGNAGE, businessTypes.signage),
      new BusinessTypeModel(
        BusinessType.SUBLIMATION,
        businessTypes.sublimation
      ),
    ];

    this.businessTypesForSelect.forEach((type) => {
      this.translationService.translateString(type.name).then((translated) => {
        type.name = translated;
      });
    });
  }

  /* eslint-disable-next-line */
  getBusinessEquipmentFormObject(
    businessId?: number,
    completed?: (formGroup: UntypedFormGroup) => any
  ) {
    if (businessId == null) {
      return this._businessEquipmentFormObject;
    }
    this.productService
      .getBusinessEquipments()
      .pipe(take(1))
      .subscribe((equipments) => {
        this.populateEquipmentsFormArray(equipments, completed);
      });
    return this._businessEquipmentFormObject;
  }

  /* eslint-disable-next-line */
  getBusinessEquipmentFormObjectPaged(
    page: number,
    pageNumber: number,
    businessId?: number,
    completed?: (formGroup: UntypedFormGroup) => any
  ) {
    if (businessId == null) {
      return this._businessEquipmentFormObject;
    }
    this.productService
      .getBusinessEquipmentsPaginated(page, pageNumber)
      .pipe(take(1))
      .subscribe((equipments) => {
        this.populateEquipmentsFormArray(equipments, completed, page === 0);
      });
    return this._businessEquipmentFormObject;
  }

  /* eslint-disable-next-line */
  private populateEquipmentsFormArray(
    equipments: any[],
    completed?: (formGroup: UntypedFormGroup) => any,
    clearExisting = true
  ): void {
    const formGroup = this._businessEquipmentFormObject.formGroup;
    const equipmentsFormArray = formGroup.get('equipments') as UntypedFormArray;
    if (clearExisting) {
      equipmentsFormArray.clear();
    }
    equipments.forEach((equipment) => {
      equipmentsFormArray.push(this.createEquipmentFormGroup(equipment));
    });

    if (completed != null) {
      completed(formGroup);
    }
  }

  /* eslint-disable-next-line */
  private createEquipmentFormGroup(equipment: any): UntypedFormGroup {
    return this.fb.group({
      serial: new UntypedFormControl(equipment.serial, [Validators.required]),
      planModel: new UntypedFormControl(equipment.planModel, [
        Validators.required,
      ]),
      id: new UntypedFormControl(equipment.id),
      familyName: new UntypedFormControl(equipment.family?.name),
    });
  }

  /* eslint-disable-next-line */
  get businessRepresentativeFormObject() {
    return this._businessRepresentativeFormObject;
  }

  /* eslint-disable-next-line */
  get businessDataFormGroup() {
    return this._businessDataFormGroup;
  }

  /* eslint-disable-next-line */
  get equipmentFormGroup() {
    return this._equipmentFormGroup;
  }

  /* eslint-disable-next-line */
  get businessRepresentativeFormGroup() {
    return this._businessRepresentativeFormGroup;
  }

  /**
   * Resets all forms
   * @param {boolean} isUpdateBusiness
   */
  resetForms(isUpdateBusiness?: boolean) {
    this.initializeFormObjects(isUpdateBusiness);
  }

  /**
   * initialize form objects!
   * @param {boolean} isUpdateBusiness
   */
  private initializeFormObjects(isUpdateBusiness?: boolean) {
    this._businessDataFormGroup =
      this.businessDataFormGroupFactory(isUpdateBusiness);

    this._businessDataFormObject = this.businessDataFormObjectFactory(
      this.businessDataFormGroup
    );

    this._equipmentFormGroup = this.businessEquipmentFormGroupFactory();
    this._businessEquipmentFormObject = this.businessEquipmentFormObjectFactory(
      this.equipmentFormGroup
    );

    this._businessRepresentativeFormGroup =
      this.businessRepresentativeFormGroupFactory();
    this._businessRepresentativeFormObject =
      this.businessRepresentativeFormObjectFactory(
        this.businessRepresentativeFormGroup
      );

    this._registerEquipmentFormGroup = this.registerEquipmentFormGroupFactory();

    this._countryEquipmentFormGroup = this.countryEquipmentFormGroupFactory();
  }

  /**
   * True if any of the forms are invalid
   * @return {boolean}
   */
  anyInvalidForm(): boolean {
    return (
      this.businessDataFormGroup.invalid ||
      this.equipmentFormGroup.invalid ||
      this.businessRepresentativeFormGroup.invalid
    );
  }

  /**
   * Invokes user service to register business
   * @param {string} recaptcha
   */
  async registerBusiness(recaptcha: string) {
    if (this.anyInvalidForm()) {
      throw new Error(this.errorMessages.cantRegisterInvalidForm);
    }
    const signUpResponse$ = this.userService.registerBusiness(
      this.accountFactory(),
      recaptcha
    );
    await lastValueFrom(signUpResponse$);
    this.resetForms();
  }

  /**
   * Invokes user service to register representative
   * @param {string} recaptcha
   * @param {string} language
   */
  async registerRepresentative(recaptcha: string, language?: string) {
    if (this.businessRepresentativeFormGroup.invalid) {
      throw new Error(this.errorMessages.cantRegisterInvalidForm);
    }

    const representative$ = this.userService.registerRepresentative(
      this.representativeFactory(language),
      recaptcha
    );
    try {
      await lastValueFrom(representative$);
      this.businessRepresentativeFormGroup.reset();
      this.inviteCurrentBusiness = undefined;
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        throw new Error(e.error.errorMessage);
      }
      throw new Error(this.errorMessages.errorTryingRegister);
    }
  }

  /**
   * @return {BusinessDTO}
   */
  businessDataDTOFromForm(): BusinessDTO {
    const businessDataValues = this.businessDataFormGroup.value;

    if (businessDataValues.businessWebsite === '') {
      businessDataValues.businessWebsite = null;
    }

    const businessDataDTO = new BusinessDTO(
      businessDataValues.businessName,
      businessDataValues.typeOfBusiness,
      businessDataValues.businessLevel,
      [],
      businessDataValues.businessContactEmail,
      businessDataValues.businessWebsite,
      businessDataValues.picture,
      businessDataValues.businessAddress,
      businessDataValues.number,
      businessDataValues.postalCode,
      businessDataValues.country,
      businessDataValues.state,
      businessDataValues.id,
      true,
      businessDataValues.phoneNumber
    );
    return businessDataDTO;
  }

  /**
   * Instantiates Account from three ADEQUATE form groups
   * @return {Account}
   */
  accountFactory(): Account {
    if (this.anyInvalidForm()) {
      throw new Error(this.errorMessages.cantInstatiateUser);
    }
    const businessDataDTO = this.businessDataDTOFromForm();

    const businessEquipmentValues = this.equipmentFormGroup.value;
    const businessEquipmentsDTO: Equipment[] = [];

    for (const equipment of businessEquipmentValues.equipments) {
      businessEquipmentsDTO.push(
        new Equipment(equipment.serial, equipment.planModel)
      );
    }

    const businessRepresentativeDTO = this.representativeFactory();

    const account = new Account(
      businessDataDTO,
      businessEquipmentsDTO,
      businessRepresentativeDTO
    );

    return account;
  }

  /**
   * Instantiates a Representative from its form group
   * The language param works only for invited users, once it isz not informed previously by the user
   * @param {string} language
   * @return {Representative}
   */
  private representativeFactory(language?: string): Representative {
    if (this.businessRepresentativeFormGroup.invalid) {
      throw new Error(this.errorMessages.cantInstatiateUser);
    }

    const businessRepresentativeValues =
      this.businessRepresentativeFormGroup.getRawValue();

    const businessRepresentative = new Representative(
      businessRepresentativeValues.name,
      businessRepresentativeValues.lastName,
      businessRepresentativeValues.email,
      businessRepresentativeValues.picture,
      businessRepresentativeValues.confirmPassword,
      this.getLanguage(language)
    );

    if (this.inviteNewBusiness) {
      businessRepresentative.businessId = this.inviteNewBusiness.id;
    }

    return businessRepresentative;
  }

  /**
   * Get language based on form value
   * @param {string} language
   * @return {string}
   */
  private getLanguage(language?: string): string | undefined {
    let res = '';
    if (this.businessDataDTOFromForm().countryId) {
      res = this.getLanguageByCountryId(
        this.businessDataDTOFromForm().countryId
      ) as string;
    } else {
      if (language) {
        this.getHashValidationDTO().subscribe((data) => {
          res = data?.invitedBusiness.country?.language as string;
        });
      } else {
        res = getTranslocoLangToBackEnd(
          this.translationService.getActiveLang()
        );
      }
    }
    return res;
  }

  /**
   * Get language by country id
   * @param {number} countryId
   * @return {string}
   */
  private getLanguageByCountryId(countryId: number) {
    const languages = this._businessDataFormObject.options.country;
    const country = languages.find((lang) => lang.id === countryId);
    return country?.language;
  }

  /**
   * @param {boolean} isUpdateBusiness
   * @return {FormGroup}
   */
  private businessDataFormGroupFactory(
    isUpdateBusiness?: boolean
  ): UntypedFormGroup {
    const freshFormGroup = new UntypedFormGroup({
      businessName: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(3),
        genericRegisterValidator('businessName'),
      ]),
      businessAddress: new UntypedFormControl('', [
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      number: new UntypedFormControl('', [genericRegisterValidator()]),
      country: new UntypedFormControl('', [Validators.required]),
      state: new UntypedFormControl('', [Validators.required]),
      postalCode: new UntypedFormControl({ value: '', disabled: true }),
      phoneNumber: new FormControl({ value: '', disabled: true }),
      typeOfBusiness: new UntypedFormControl(''),
      businessContactEmail: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
        emailPattern(),
        Validators.minLength(3),
        genericRegisterValidator('email'),
      ]),
      businessWebsite: new UntypedFormControl('', [
        urlValidator(),
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      picture: new UntypedFormControl(''),
      businessLevel: new UntypedFormControl(''),
    });

    let previousCountry = '';
    freshFormGroup.get('country')!.valueChanges.subscribe((country) => {
      if (country === previousCountry) {
        return;
      }
      previousCountry = country;
      const postalCodeControl = freshFormGroup.get('postalCode')!;
      postalCodeControl.clearValidators();
      postalCodeControl.setValidators([
        Validators.minLength(3),
        genericRegisterValidator(),
        postalCodeValidator(country),
      ]);
      postalCodeControl.enable();
      postalCodeControl.updateValueAndValidity();

      const phoneNumberControl = freshFormGroup.get('phoneNumber')!;
      phoneNumberControl.clearValidators();
      // const dialCode = this.findDialCodeByCountry(country);
      // phoneNumberControl.setValidators([phoneNumberValidator(dialCode!)]);
      phoneNumberControl.setValidators([
        Validators.required,
        Validators.minLength(7),
        Validators.maxLength(18),
        genericRegisterValidator(),
      ]);
      phoneNumberControl.enable();
      phoneNumberControl.updateValueAndValidity();
    });

    const businessContactEmailField =
      freshFormGroup.controls['businessContactEmail'];

    if (!isUpdateBusiness) {
      businessContactEmailField.addAsyncValidators(
        asyncValidator(
          this.repeatedBusinessEmailValidationFunction(),
          'invalid-repeated-business-email'
        )
      );
    }

    return freshFormGroup;
  }

  /**
   * @param {number} countryId
   * @return {any}
   */
  private findDialCodeByCountry(countryId: number) {
    const country = this.countries.find((c) => c.id === countryId);

    const phonePrefix = phonePrefixes.find((p) => p.code === country?.initials);
    return phonePrefix?.dial_code;
  }

  /**
   * Resets register equipment form group
   */
  resetRegisterEquipmentFormGroup() {
    this._registerEquipmentFormGroup = this.registerEquipmentFormGroupFactory();
  }

  /**
   * @return {TRegisterEquipmentFormGroup}
   */
  private registerEquipmentFormGroupFactory(): TRegisterEquipmentFormGroup {
    const registerEquipmentFormGroup = new FormGroup<TRegisterEquipmentForm>({
      placeOfPurchase: new FormControl('', [
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      installationDate: new FormControl(null),
      businessName: new FormControl('', [
        Validators.minLength(3),
        genericRegisterValidator('businessName'),
      ]),
      representativeName: new FormControl('', [
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      representativeLastName: new FormControl('', [
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      businessEmail: new FormControl('', [
        Validators.minLength(3),
        emailPattern(),
        genericRegisterValidator('email'),
      ]),
      country: new FormControl(''),
      stateOrRegion: new FormControl(''),
      primaryPhone: new FormControl('', [
        Validators.maxLength(18),
        Validators.minLength(7),
        genericRegisterValidator(),
        Validators.required,
      ]),
      phoneType: new FormControl('mobile', Validators.required),
      agree: new FormControl(false),
    });
    (
      Object.keys(
        registerEquipmentFormGroup.controls
      ) as (keyof TRegisterEquipmentForm)[]
    ).forEach((key) => {
      // eslint-disable-next-line security/detect-object-injection
      const control = registerEquipmentFormGroup.controls[key];
      if (key === 'agree') {
        control.addValidators(Validators.requiredTrue);
      } else if (key !== 'primaryPhone' && key !== 'phoneType') {
        control.addValidators(Validators.required);
      }
      control.updateValueAndValidity();
    });

    // set up listeners that fill other forms
    registerEquipmentFormGroup.controls.businessName.valueChanges.subscribe(
      (value) => {
        this.businessDataFormGroup.controls['businessName'].setValue(value);
      }
    );
    registerEquipmentFormGroup.controls.businessEmail.valueChanges.subscribe(
      (value) => {
        this.businessDataFormGroup.controls['businessContactEmail'].setValue(
          value
        );
      }
    );
    registerEquipmentFormGroup.controls.country.valueChanges.subscribe(
      (value) => {
        const countryControl = this.businessDataFormGroup.controls['country'];
        if (value !== countryControl.value) {
          countryControl.setValue(value);
        }
      }
    );
    registerEquipmentFormGroup.controls.stateOrRegion.valueChanges.subscribe(
      (value) => {
        this.businessDataFormGroup.controls['state'].setValue(value);
      }
    );
    registerEquipmentFormGroup.controls.primaryPhone.valueChanges.subscribe(
      (value) => {
        const phoneControl = this.businessDataFormGroup.controls['phoneNumber'];
        if (value !== phoneControl.value) {
          phoneControl.setValue(value);
        }
      }
    );
    registerEquipmentFormGroup.controls.representativeName.valueChanges.subscribe(
      (value) => {
        this.businessRepresentativeFormGroup.controls['name'].setValue(value);
      }
    );
    registerEquipmentFormGroup.controls.representativeLastName.valueChanges.subscribe(
      (value) => {
        this.businessRepresentativeFormGroup.controls['lastName'].setValue(
          value
        );
      }
    );

    const businessContactEmailField =
      registerEquipmentFormGroup.controls['businessEmail'];

    businessContactEmailField.addAsyncValidators(
      asyncValidator(
        this.repeatedBusinessEmailValidationFunction(),
        'invalid-repeated-business-email'
      )
    );

    return registerEquipmentFormGroup;
  }

  /**
   * @return {TCountryEquipmentFormGroup}
   */
  private countryEquipmentFormGroupFactory(): TCountryEquipmentFormGroup {
    const countryFormGroup = new FormGroup<TCountryEquipmentForm>({
      country: new FormControl(''),
    });
    (
      Object.keys(countryFormGroup.controls) as (keyof TCountryEquipmentForm)[]
    ).forEach((key) => {
      // eslint-disable-next-line security/detect-object-injection
      const control = countryFormGroup.controls[key];
      control.updateValueAndValidity();
    });

    countryFormGroup.controls.country.valueChanges.subscribe((value) => {
      // NOSONAR
      const countryControl = this.businessDataFormGroup.controls['country'];
      if (value !== countryControl.value) {
        countryControl.setValue(value);
      }
    });

    return countryFormGroup;
  }
  /**
   * Resets business equipment form group
   */
  resetBusinessEquipmentFormGroup() {
    this._businessEquipmentFormObject.formGroup =
      this.businessEquipmentFormGroupFactory();
  }

  /**
   * @return {FormGroup}
   */
  private businessEquipmentFormGroupFactory(): UntypedFormGroup {
    return new UntypedFormGroup({
      equipments: this.fb.array([this.createEquipmentFormTuple()]),
    });
  }

  /**
   * Creates a new equipment form tuple and returns it
   * @return {FormControl}
   */
  createEquipmentFormTuple(): FormGroup<{
    serial: FormControl;
    planModel: FormControl;
    unremovable?: FormControl;
    id?: FormControl;
  }> {
    const serial = new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(18),
      Validators.pattern('^[a-zA-Z0-9]*$'),
      serialValidator(this._serialNumber),
    ]);
    const planModel = new FormControl('');
    const equipmentFormTuple = new FormGroup({
      serial,
      planModel,
    });
    let previousValue = serial.value;
    serial.valueChanges.subscribe((currentValue) => {
      if (previousValue !== currentValue) {
        planModel.setValue('');
      }
      previousValue = currentValue;
    });

    return equipmentFormTuple;
  }

  /**
   * @return {FormGroup}
   */
  private businessRepresentativeFormGroupFactory(): UntypedFormGroup {
    const freshFormGroup = new UntypedFormGroup({
      name: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      lastName: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(3),
        genericRegisterValidator(),
      ]),
      email: new UntypedFormControl('', [
        Validators.required,
        emailPattern(),
        Validators.minLength(3),
        genericRegisterValidator('email'),
      ]),
      confirmEmail: new UntypedFormControl('', [
        Validators.required,
        emailPattern(),
        Validators.minLength(3),
        genericRegisterValidator('email'),
      ]),
      password: new UntypedFormControl('', [
        Validators.required,
        passwordValidator(),
      ]),
      confirmPassword: new UntypedFormControl('', [Validators.required]),
      picture: new UntypedFormControl(''),
    });

    sameValueTwoFields(
      freshFormGroup.get('password')!,
      freshFormGroup.get('confirmPassword')!,
      [Validators.required]
    );

    sameValueTwoFields(
      freshFormGroup.get('email')!,
      freshFormGroup.get('confirmEmail')!,
      [Validators.required]
    );
    return freshFormGroup;
  }

  /**
   * Builds business data form object
   * @param {FormGroup} formGroup
   * @return {BusinessDataFormObject | any}
   */
  businessDataFormObjectFactory(
    formGroup: UntypedFormGroup
  ): BusinessDataFormObject {
    const dataLabels = new BusinessDataLabels(
      'business-name',
      'business-address',
      'number',
      'country',
      'state-sub-region',
      'postal-code',
      'phone',
      'type-of-business',
      'business-email',
      'business-website',
      ['upload-company-logo', 'change-image'],
      ['upload-company-logo-alt', 'uploaded-company-logo-alt']
    );
    const countryOptions = new BusinessDataOptions([], []);
    this.subsink.add(this.translationService.translateObject$(dataLabels));

    return new BusinessDataFormObject(dataLabels, formGroup, countryOptions);
  }

  /**
   * Builds business data form object
   * @param {FormGroup} formGroup
   * @return {BusinessEquipmentFormObject}
   */
  businessEquipmentFormObjectFactory(
    formGroup: UntypedFormGroup
  ): BusinessEquipmentFormObject {
    const equipmentLabels = new BusinessEquipmentLabels(
      'printer-serial-number',
      'model',
      'add-equipment',
      'remove-equipment',
      'invalid-serial-error-message',
      'serial-server-error',
      'equipment-exists-error'
    );

    this.subsink.add(this.translationService.translateObject$(equipmentLabels));

    return new BusinessEquipmentFormObject(equipmentLabels, formGroup);
  }

  /**
   * Builds business representative form object
   * @param {FormGroup} formGroup
   * @return {BusinessRepresentativeFormObject}
   */
  businessRepresentativeFormObjectFactory(
    formGroup: UntypedFormGroup
  ): BusinessRepresentativeFormObject {
    const businessRepresentativeLabels = new BusinessRepresentativeLabels(
      'name',
      'last-name',
      'email-address',
      'confirm-email-address',
      'password',
      'confirm-password',
      'representative-informative-text',
      'email-inform',
      'and',
      'password-inform',
      'representative-informative-text-2',
      'representative-informative-text-3',
      'password',
      'register-agree',
      'terms-conditions',
      'privacy-policy',
      ['upload-image', 'change-image'],
      ['upload-image-alt', 'uploaded-image-alt']
    );

    this.subsink.add(
      this.translationService.translateObject$(businessRepresentativeLabels)
    );

    return new BusinessRepresentativeFormObject(
      formGroup,
      businessRepresentativeLabels
    );
  }

  /**
   * Updates Business Data
   */
  async updateBusinessData() {
    if (this.businessDataFormGroup.invalid) {
      throw new Error(this.errorMessages.candUpdateInvalidForm);
    }
    const businessDataDTO = this.businessDataDTOFromForm();
    if (businessDataDTO.state === 'N/A') {
      businessDataDTO.state = '';
    }
    const businessData$ = this.userService.updateBusinessData(businessDataDTO);
    try {
      const businessData = await lastValueFrom(businessData$);
      this.topHeaderService.business?.update(businessData);
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        throw new Error(e.error.errorMessage);
      }
      throw new Error(this.errorMessages.tryingUpdateBusinessError);
    }
  }

  /**
   * @return { Function<Promise<boolean>> }
   */
  private repeatedBusinessEmailValidationFunction(): (
    arg0: any
  ) => Promise<boolean> {
    return async (businessContactEmail: string) => {
      try {
        return await firstValueFrom(
          this.userService.validateBusinessEmail(businessContactEmail)
        );
      } catch (err) {
        return false;
      }
    };
  }

  /**
   * @return {Observable<string>}
   */
  getTermsOfUseAndPrivacyConditionsURLs() {
    return this.userService.getTermsOfUseAndPrivacyConditionsURLs();
  }

  /**
   * @param {string} hash
   * @return {Observable<HashValidationDTO>}
   */
  validateHash(hash: string) {
    return this.userService.validateHash(hash);
  }

  /**
   * @param {AbstractControl} serial
   * @param {AbstractControl} equipment
   * @param {string} countryInitials
   * @param {boolean} isRegister
   * @param {Promise<void>} saveEquipment
   * @param {Promise<void>} nextPhase
   */
  validateSerial(
    serial: AbstractControl,
    equipment: AbstractControl,
    countryInitials: string,
    isRegister?: boolean,
    saveEquipment?: () => Promise<void>,
    nextPhase?: () => Promise<void>
  ): void {
    this.productService
      .validateSerial([serial.value], countryInitials)
      .pipe(take(1))
      .subscribe({
        next: ([validationResponse, ..._]) => {
          // NOSONAR
          const currentEquipment =
            this._businessEquipmentFormObject.formGroup.value.equipments[0];
          switch (validationResponse?.status) {
            case SerialModelValidationStatus.NOT_REGISTERED_IN_SAP:
              this.businessEquipmentFormPhase = 'EPSON_UNREGISTERED_PENDING';
              equipment
                .get('planModel')
                ?.setValue(validationResponse.planModel);
              if (nextPhase) {
                nextPhase();
              }
              return;
            case SerialModelValidationStatus.NOT_REGISTERED:
              this.businessEquipmentFormPhase = 'LNR_REGISTER';
              if (isRegister) {
                this.equipmentRegisterFeedback = 'eligible';
              }
              equipment
                .get('planModel')
                ?.setValue(validationResponse.planModel);
              if (saveEquipment) {
                saveEquipment();
              }
              if (nextPhase) {
                nextPhase();
              }
              return;
            case SerialModelValidationStatus.ALREADY_REGISTERED:
              this.businessEquipmentFormPhase = 'LNR_REGISTER';
              this.equipmentRegisterFeedback = undefined;
              equipment.enable();
              this.disableCountryField = false;
              serial.setErrors(
                errorObjectBuilder('serial-already-registered-on-epson-rewards')
              );
              this.dialogService.openDialog({
                title: this.labels.couldNotRegister,
                content: this.labels.printerAlreadyRegitered,
                successButtonText: this.labels.ok,
              });
              if (nextPhase) {
                nextPhase();
              }
              return;
            case SerialModelValidationStatus.MODEL_NOT_FOUND:
              this.businessEquipmentFormPhase = 'LNR_REGISTER';
              this.equipmentRegisterFeedback = undefined;
              equipment.enable();
              serial.setErrors(errorObjectBuilder('model-not-found'));
              this.dialogService.openDialog({
                title: this.labels.couldNotRegister,
                content: this.labels.invalidModelNumber,
                successButtonText: this.labels.ok,
              });
              this.disableCountryField = false;
              if (nextPhase) {
                nextPhase();
              }
              return;
            default:
              equipment.enable();
              this.disableCountryField = false;
              this.dialogService.openDialog({
                title: this.labels.couldNotCheckPrinterSerialData,
                content: this.labels.noCommPrinterDatabase,
                successButtonText: this.labels.ok,
              });
              if (nextPhase) {
                nextPhase();
              }
          }
        },
        error: (err: Error) => {
          switch (err.constructor) {
            case InvalidSerialException:
              this.refreshAndSetInvalidSerialException(equipment, serial);
              if (nextPhase) {
                nextPhase();
              }
              return;
            case InvalidSerialNumberException:
              equipment.enable();
              this.disableCountryField = false;
              this.dialogService.openDialog({
                title: this.labels.couldNotRegister,
                content: this.labels.invalidSerialNumber,
                successButtonText: this.labels.ok,
              });
              if (nextPhase) {
                nextPhase();
              }
              return;
            default:
              equipment.enable();
              this.disableCountryField = false;
              this.dialogService.openDialog({
                title: this.labels.couldNotCheckPrinterSerialData,
                content: this.labels.noCommPrinterDatabase,
                successButtonText: this.labels.ok,
              });
              if (nextPhase) {
                nextPhase();
              }
          }
        },
      });
  }

  /**
   * Refresh form and set error to invalid serial number
   * @param {AbstractControl} equipment
   * @param {AbstractControl} serial
   */
  private refreshAndSetInvalidSerialException(
    equipment: AbstractControl,
    serial: AbstractControl
  ) {
    equipment.enable();
    this.disableCountryField = false;
    this.businessEquipmentFormPhase = 'LNR_REGISTER';
    this.equipmentRegisterFeedback = undefined;
    serial.setErrors(errorObjectBuilder('invalid-serial-number'));
  }

  /**
   * Find country by id
   * @param {number} id
   * @return {CountryList}
   */
  public findCountryById(id: number): CountryList | undefined {
    return this.countries.find((country) => country.id === id);
  }
}
